var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ColorStyleWidth } from '@shared/commons/DynProperty';
import { PenStyle } from '@shared/commons/Graphics';
import { AnalyzerProbabilityCalculationMode } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionAnalyzer/AnalyzerProbabilityCalculationMode';
import { Resources } from '@shared/localizations/Resources';
import { TerceraChartAnalyzerInfoWindowRenderer } from './Renderers/InfoWindow/TerceraChartAnalyzerInfoWindowRenderer';
import { TerceraChartBaseCrossHairRenderer } from './Renderers/TerceraChartBaseCrossHairRenderer';
import { TerceraChartRangeRenderer } from './Renderers/TerceraChartRangeRenderer';
import { LineStyle } from './Renderers/TerceraChartStyledLineRenderer';
import { TerceraChartStyledVerticalLineRenderer } from './Renderers/TerceraChartStyledVerticalLineRenderer';
import { TerceraChartAggregatedXYSeries, TerceraChartXYSeries, XYSeriesData } from './Series/TerceraChartXYSeries';
import { TerceraXYChart } from './TerceraXYChart';
import { ChartZoomRendererButtons } from './Renderers/TerceraChartZoomRenderer';
import { AnalyzerChartType } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionAnalyzer/AnalyzerChartType';
export class OptionAnalyzerChart extends TerceraXYChart {
    constructor(ctx, terceraChartPanelContext) {
        super(ctx, terceraChartPanelContext);
    }
    CreateMainWindow() {
        const mainWindow = super.CreateMainWindow();
        if (!isNullOrUndefined(mainWindow.leftYScaleRenderer)) {
            mainWindow.leftYScaleRenderer.Step = 0.01;
            mainWindow.leftYScaleRenderer.Precision = 2;
        }
        if (!isNullOrUndefined(mainWindow.rightYScaleRenderer)) {
            mainWindow.rightYScaleRenderer.Step = 0.01;
            mainWindow.rightYScaleRenderer.Precision = 2;
        }
        return mainWindow;
    }
    CreateWindowsContainer() {
        const container = super.CreateWindowsContainer();
        container.zoomRenderer.CurrentVisibleButtons =
            ChartZoomRendererButtons.StepZoomIn | ChartZoomRendererButtons.StepZoomOut;
        return container;
    }
    InitializeRenderers() {
        super.InitializeRenderers();
        this.basePriceLineRenderer = new TerceraChartStyledVerticalLineRenderer(this);
        this.infoWindowRenderer = new TerceraChartAnalyzerInfoWindowRenderer(this);
        this.rangeRenderer = new TerceraChartRangeRenderer(this);
        this._forceUpdateInitiators.push(this.infoWindowRenderer);
        this.mainWindow.Renderers.push(this.basePriceLineRenderer);
        this.mainWindow.Renderers.push(this.InitializeCrossHairRenderer());
        this.mainWindow.Renderers.push(this.infoWindowRenderer);
        this.mainWindow.Renderers.push(this.rangeRenderer);
    }
    InitializeCrossHairRenderer() {
        return new TerceraChartBaseCrossHairRenderer(this);
    }
    DrawNoData(gr) {
        this.NoDataMessage = Resources.getResource('panel.optionAnalyzer.chart.NoData');
        super.DrawNoData(gr);
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.refreshVolatilityLines();
    }
    Localize() {
        super.Localize();
        const rangeRenderer = this.rangeRenderer;
        rangeRenderer.leftBorder.Label = rangeRenderer.rightBorder.Label =
            Resources.getResource('property.Probability');
    }
    Properties() {
        return this.infoWindowRenderer.Properties();
    }
    callBack(properties) {
        this.infoWindowRenderer.callBack(properties);
    }
    attachOptionTrader(optionTrader) {
        this.optionTrader = optionTrader;
    }
    detachOptionTrader() {
        if (isNullOrUndefined(this.optionTrader)) {
            return;
        }
        this.optionTrader = null;
    }
    set(chartData) {
        this.updateBasePriceLines(chartData.underlierPrice);
        const series = this.createSeries(chartData);
        this.setSeries(series);
        this.refreshVolatilityLines();
    }
    update(chartData) {
        this.updateBasePriceLines(chartData.underlierPrice);
        const series = this.createSeries(chartData);
        super.updateSeries(series);
        this.refreshVolatilityLines();
        this.IsDirty(true);
    }
    updateBasePriceLines(price) {
        const basePriceLineSeries = new TerceraChartXYSeries();
        basePriceLineSeries.data.push(new XYSeriesData(price, 0));
        this.basePriceLineRenderer.Series = basePriceLineSeries;
        this.infoWindowRenderer.setDefaultData(price, 0);
    }
    createSeries(chartData) {
        const series = new TerceraChartAggregatedXYSeries();
        const zeroLineSeries = new TerceraChartXYSeries();
        const timeLineSeries = new TerceraChartXYSeries();
        const intrinsicSeries = new TerceraChartXYSeries();
        const volatilitySeries = [];
        for (let i = 0; i < chartData.zeroLineSeries.length; i++) {
            zeroLineSeries.data.push(new XYSeriesData(chartData.zeroLineSeries[i].x, chartData.zeroLineSeries[i].y));
        }
        for (let i = 0; i < chartData.timeLineSeries.length; i++) {
            timeLineSeries.data.push(new XYSeriesData(chartData.timeLineSeries[i].x, chartData.timeLineSeries[i].y));
        }
        for (let i = 0; i < chartData.intrinsicSeries.length; i++) {
            intrinsicSeries.data.push(new XYSeriesData(chartData.intrinsicSeries[i].x, chartData.intrinsicSeries[i].y));
        }
        for (let i = 0; i < chartData.volatilitySeries.length; i++) {
            const volatilitySeriesItem = new TerceraChartXYSeries();
            for (let j = 0; j < chartData.volatilitySeries[i].length; j++) {
                volatilitySeriesItem.data.push(new XYSeriesData(chartData.volatilitySeries[i][j].x, chartData.volatilitySeries[i][j].y));
            }
            volatilitySeries.push(volatilitySeriesItem);
        }
        series.data.push(zeroLineSeries, timeLineSeries, intrinsicSeries, ...volatilitySeries);
        return series;
    }
    refreshVolatilityLines() {
        if (isNullOrUndefined(this.optionTrader)) {
            return;
        }
        const optionAnalyzer = this.optionTrader.optionAnalyzer;
        const basePriceLineRenderer = this.mainWindow.GetRenderer(TerceraChartStyledVerticalLineRenderer);
        if (!isNullOrUndefined(basePriceLineRenderer)) {
            basePriceLineRenderer.ColorStyleWidth = new ColorStyleWidth(optionAnalyzer.underlierPriceColor, optionAnalyzer.underlierPriceStyle, optionAnalyzer.underlierPriceWidth);
        }
        const zeroLineRenderer = this.getLineRendererByIndex(OptionAnalyzerChart.ZERO_LINE);
        if (!isNullOrUndefined(zeroLineRenderer)) {
            zeroLineRenderer.LineStyle = LineStyle.Line;
            zeroLineRenderer.ColorStyleWidth = new ColorStyleWidth(optionAnalyzer.zeroLineColor, optionAnalyzer.zeroLineStyle, optionAnalyzer.zeroLineWidth);
        }
        const timeLineRenderer = this.getLineRendererByIndex(OptionAnalyzerChart.TIME_LINE);
        if (!isNullOrUndefined(timeLineRenderer)) {
            timeLineRenderer.ColorStyleWidth = new ColorStyleWidth(optionAnalyzer.timeLineColor, optionAnalyzer.timeLineStyle, optionAnalyzer.timeLineWidth);
        }
        const intrinsicLineRenderer = this.getLineRendererByIndex(OptionAnalyzerChart.INTRINSIC_LINE);
        if (!isNullOrUndefined(intrinsicLineRenderer)) {
            intrinsicLineRenderer.LineStyle = LineStyle.Line;
            intrinsicLineRenderer.ColorStyleWidth = new ColorStyleWidth(optionAnalyzer.intrinsicColor, optionAnalyzer.intrinsicStyle, optionAnalyzer.intrinsicWidth);
        }
        const volatilityLineIndex = OptionAnalyzerChart.INTRINSIC_LINE + 1;
        for (let i = 0; i < optionAnalyzer.volatilityLines.length; i++) {
            const lineRenderer = this.getLineRendererByIndex(volatilityLineIndex + i);
            if (!isNullOrUndefined(lineRenderer)) {
                lineRenderer.Visible = optionAnalyzer.volatilityLines[i].isActive;
                lineRenderer.ColorStyleWidth = new ColorStyleWidth(optionAnalyzer.volatilityLines[i].color, PenStyle.SimpleChart, optionAnalyzer.volatilityLines[i].width);
            }
        }
        this.infoWindowRenderer.isNeedForceUpdate = true;
        this.IsDirty(true);
    }
    refreshProbabilityLines() {
        const rangeRenderer = this.rangeRenderer;
        const optionTrader = this.optionTrader;
        const underierPrice = this.optionTrader.underlier.Level1.GetLastPrice(this.optionTrader.account);
        rangeRenderer.leftBorder.AdditionalLabel = rangeRenderer.rightBorder.AdditionalLabel = '';
        switch (optionTrader.optionAnalyzer.probabilityCalculationMode) {
            case AnalyzerProbabilityCalculationMode.None:
                rangeRenderer.Visible = false;
                break;
            case AnalyzerProbabilityCalculationMode.Single:
                rangeRenderer.leftBorder.Value = underierPrice;
                rangeRenderer.leftBorder.isVisible = true;
                rangeRenderer.rightBorder.Value = NaN;
                rangeRenderer.rightBorder.isVisible = false;
                rangeRenderer.Visible = true;
                break;
            case AnalyzerProbabilityCalculationMode.OR:
            case AnalyzerProbabilityCalculationMode.AND:
                rangeRenderer.leftBorder.Value = underierPrice - underierPrice * 0.01;
                rangeRenderer.leftBorder.isVisible = true;
                rangeRenderer.rightBorder.Value = underierPrice + underierPrice * 0.01;
                rangeRenderer.rightBorder.isVisible = true;
                rangeRenderer.Visible = true;
                break;
            default:
                rangeRenderer.leftBorder.Value = NaN;
                rangeRenderer.leftBorder.isVisible = false;
                rangeRenderer.rightBorder.Value = NaN;
                rangeRenderer.rightBorder.isVisible = false;
                rangeRenderer.Visible = false;
                break;
        }
        this.IsDirty(true);
    }
    refreshProbabilityValuesAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const optionTrader = this.optionTrader;
            const rangeRenderer = this.rangeRenderer;
            const probability = yield optionTrader.getOptionAnalyzerProbabilityDataAsync(rangeRenderer.leftBorder.Value, rangeRenderer.rightBorder.Value);
            const p1FormattedValue = isValidNumber(probability.p1) ? `${(probability.p1 * 100).toFixed(2)}%` : '';
            const p2FormattedValue = isValidNumber(probability.p2) ? `${(probability.p2 * 100).toFixed(2)}%` : '';
            switch (optionTrader.optionAnalyzer.probabilityCalculationMode) {
                case AnalyzerProbabilityCalculationMode.None:
                    rangeRenderer.leftBorder.AdditionalLabel = rangeRenderer.rightBorder.AdditionalLabel = '';
                    break;
                case AnalyzerProbabilityCalculationMode.Single:
                    rangeRenderer.leftBorder.AdditionalLabel = `: ${p1FormattedValue}`;
                    rangeRenderer.rightBorder.AdditionalLabel = '';
                    break;
                case AnalyzerProbabilityCalculationMode.OR:
                    rangeRenderer.leftBorder.AdditionalLabel = `: ${p1FormattedValue}`;
                    rangeRenderer.rightBorder.AdditionalLabel = `: ${p1FormattedValue}`;
                    break;
                case AnalyzerProbabilityCalculationMode.AND:
                    rangeRenderer.leftBorder.AdditionalLabel = `1: ${p1FormattedValue}`;
                    rangeRenderer.rightBorder.AdditionalLabel = `2: ${p2FormattedValue}`;
                    break;
            }
            this.IsDirty(true);
        });
    }
    resetXScale() {
        const instrument = this.optionTrader.underlier;
        const xScaleRenderer = this.windowsContainer.xScaleRenderer;
        xScaleRenderer.Step = instrument.PointSize;
        xScaleRenderer.Precision = instrument.Precision;
        this.updateMinMaxXScaleBorders();
        this.IsDirty(true);
    }
    resetYScale() {
        this.updateMinMaxYScaleBorders();
    }
    resetZoom() {
        if (this.optionTrader.optionAnalyzer.chartType === AnalyzerChartType.PL) {
            this.mainWindow.ZoomIn();
            this.mainWindow.ZoomIn();
        }
    }
    resetScales() {
        this.resetXScale();
        this.resetYScale();
        this.resetZoom();
    }
    getOptionAnalyzerInfoData(x) {
        return this.optionTrader.getOptionAnalyzerInfoData(x);
    }
    getIntrinsicLineColor() {
        return this.optionTrader.optionAnalyzer.intrinsicColor;
    }
    getTimeLineColor() {
        return this.optionTrader.optionAnalyzer.timeLineColor;
    }
    getVolatilityLineColor(index) {
        return this.optionTrader.optionAnalyzer.volatilityLines[index].color;
    }
    getVolatilityLineVisibility(index) {
        return this.optionTrader.optionAnalyzer.volatilityLines[index].isActive;
    }
}
OptionAnalyzerChart.ZERO_LINE = 0;
OptionAnalyzerChart.TIME_LINE = 1;
OptionAnalyzerChart.INTRINSIC_LINE = 2;
